<template>
  <tr v-on:submit.prevent="add">
    <td>
      <div class="control">
        <div
          v-if="includeGroups"
          class="select"
          :class="{ 'is-danger': fieldErrors.identifier }"
        >
          <select
            v-model="fields.type"
            :disabled="!isAdmin || loading || undefined"
            required
          >
            <option
              class="is-capitalized"
              v-for="type in membershipType"
              :key="type"
              :value="type"
            >
              {{ type }}
            </option>
          </select>
        </div>
      </div>
    </td>
    <td>
      <div class="control is-expanded">
        <input
          :type="fields.type === 'user' ? 'email' : 'text'"
          :placeholder="fields.type === 'user' ? 'Email address' : 'Group ID'"
          class="input"
          v-model="fields.identifier"
          :class="{ 'is-danger': fieldErrors.identifier }"
          :disabled="!isAdmin || loading || undefined"
          required
        />
        <template v-if="fieldErrors.identifier">
          <p
            class="help is-danger"
            v-for="err in fieldErrors.identifier"
            :key="err"
          >
            {{ err }}
          </p>
        </template>
      </div>
    </td>
    <td>
      <div class="control">
        <div
          class="select"
          :class="{ 'is-danger': fieldErrors.level }"
        >
          <select
            v-model="fields.level"
            :disabled="!isAdmin || loading || undefined"
            required
          >
            <option
              v-for="role in ROLES"
              :key="role.value"
              :value="role.value"
              :title="role.help"
            >
              {{ role.display }}
            </option>
          </select>
          <template v-if="fieldErrors.level">
            <p
              class="help is-danger"
              v-for="err in fieldErrors.level"
              :key="err"
            >
              {{ err }}
            </p>
          </template>
        </div>
      </div>
    </td>
    <td>
      <div class="control">
        <button
          class="button is-primary"
          :class="{ 'is-loading': loading }"
          :disabled="!isAdmin || loading || undefined"
          :title="isAdmin ? 'Grant access permissions' : undefined"
          v-on:click.prevent="add"
        >
          <i class="icon-plus"></i>
        </button>
      </div>
    </td>
  </tr>
</template>

<script lang="ts">
import { isAxiosError } from 'axios'
import { mapActions } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import { type MembershipContentType, createMembership } from '@/api'
import { ROLES } from '@/config'
import { errorParser } from '@/helpers'
import { useNotificationStore } from '@/stores'
import type { UUID } from '@/types'

export default defineComponent({
  emits: ['update'],
  props: {
    contentType: {
      type: String as PropType<MembershipContentType>,
      required: true,
    },
    contentId: {
      type: String as PropType<UUID>,
      required: true,
    },
    includeGroups: {
      type: Boolean,
      default: true,
    },
    isAdmin: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    ROLES,
    membershipType: ['user', 'group'],
    loading: false,
    fields: {
      type: 'user',
      identifier: '',
      level: ROLES.guest.value,
    },
    fieldErrors: {} as { identifier?: string[]; level?: string[] },
  }),
  methods: {
    ...mapActions(useNotificationStore, ['notify']),
    async add() {
      if (this.loading || !this.isAdmin) return

      const identifier = this.fields.identifier.trim()
      const level = this.fields.level

      // Detect invalid inputs before sending the form
      this.fieldErrors = {}
      if (!identifier) this.fieldErrors.identifier = ['This field may not be blank.']
      if (!level) this.fieldErrors.level = ['A member must be given an access level.']
      if (!identifier || !level) return

      this.loading = true
      try {
        const payload = {
          level,
          content_type: this.contentType,
          content_id: this.contentId,
        }

        if (this.fields.type === 'user')
          await createMembership({ ...payload, user_email: identifier })
        else if (this.fields.type === 'group')
          await createMembership({ ...payload, group_id: identifier })
        else throw new Error('Unexpected membership type')

        // Send success notification
        this.notify({ type: 'success', text: 'The member was successfully added.' })
        // update store membersPage
        this.$emit('update')
        // empty form fields
        this.fields.identifier = ''
        this.fields.level = ROLES.guest.value
        this.fieldErrors = {}
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 400 && err.response.data) {
          // handle field level errors
          const { level, ...unhandledErrors } = err.response.data
          // Destructuring snake_case keys is not allowed by eslint
          const identifier = unhandledErrors.group_id || unhandledErrors.user_email
          this.fieldErrors = { level, identifier }

          delete unhandledErrors.group_id
          delete unhandledErrors.user_email
          const unhandledMessages = Object.values(unhandledErrors)
          if (unhandledMessages.length > 0) {
            // Handle generic errors e.g. an already existing membership
            this.notify({ type: 'error', text: unhandledMessages.join(', ') })
            this.fields.identifier = ''
          }
        } else {
          this.notify({ type: 'error', text: errorParser(err) })
        }
      } finally {
        this.loading = false
      }
    },
  },
})
</script>

<style scoped>
.field .email.control {
  max-width: 32ch;
}
</style>
